import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { Check, ChevronDown, User, ClipboardList, GraduationCap, Sparkles } from "lucide-react";

const VIEWS = {
  player: { label: "Player", icon: User, home: "/profile", color: "text-pb-cyan", border: "border-pb-cyan/50" },
  td: { label: "Tournament Director", short: "TD View", icon: ClipboardList, home: "/tournaments", color: "text-pb-red", border: "border-pb-red/50" },
  coach: { label: "Coach", short: "Coach View", icon: GraduationCap, home: "/coaches-corner", color: "text-amber-400", border: "border-amber-500/50" },
};

export default function ViewSwitcher() {
  const { realUser, activeView, setActiveView, availableViews, canSwitchView } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!realUser) return null;
  const isAdmin = realUser.role === "admin";
  const cur = VIEWS[activeView] || VIEWS.player;

  const switchTo = (v) => {
    setOpen(false);
    if (v === activeView) return;
    setActiveView(v);
    navigate(VIEWS[v].home);
    toast.success(`Switched to ${VIEWS[v].label} view`);
  };

  const staticBlock = (
    <div className="flex items-center gap-2.5">
      <Avatar src={realUser.avatar} name={realUser.name} size={36} testId="header-avatar" />
      <div className="text-right leading-none hidden sm:block">
        <div className="text-sm font-semibold">{realUser.name}</div>
        <div className="text-[10px] uppercase tracking-widest text-pb-red">{realUser.membership} member</div>
      </div>
    </div>
  );
  if (isAdmin) return staticBlock;

  return (
    <div className="relative">
      <button data-testid="view-switcher-btn" onClick={() => setOpen((o) => !o)} title="Switch view" aria-expanded={open}
        className="flex items-center gap-2 sm:gap-2.5 group">
        <span className="relative inline-flex">
          <Avatar src={realUser.avatar} name={realUser.name} size={36} testId="header-avatar" />
          <span className={`absolute -bottom-1 -right-1 bg-black border ${cur.border} rounded-full p-[3px]`}>
            <ChevronDown className={`w-2.5 h-2.5 ${cur.color} transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        </span>
        <span className="text-right leading-none hidden sm:block" data-no-translate>
          <span className="block text-sm font-semibold">{realUser.name}</span>
          <span className={`block text-[9px] uppercase tracking-widest mt-0.5 ${activeView === "player" ? "text-pb-red" : cur.color}`} data-testid="current-view-label">
            {activeView === "player" ? `${realUser.membership} member` : cur.short}
          </span>
        </span>
      </button>
      {open && <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />}
      <div className={`absolute right-0 top-12 z-[65] w-64 bg-black/95 backdrop-blur-md border border-pb-border shadow-[0_12px_40px_rgba(0,0,0,.75)] ${open ? "animate-fade-up" : "hidden"}`}
        data-testid="view-switcher-dropdown">
        <div className="px-3 pt-2.5 pb-1.5 text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Switch View — one account, all your hats</div>
        {availableViews.map((v) => {
          const V = VIEWS[v];
          const active = v === activeView;
          return (
            <button key={v} data-testid={`switch-view-${v}`} onClick={() => switchTo(v)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${active ? `${V.color} bg-white/5` : "text-zinc-300 hover:bg-white/5"}`}>
              <V.icon className={`w-4 h-4 ${V.color}`} />
              <span className="flex-1 text-left">{V.label}</span>
              {active && <Check className="w-3.5 h-3.5" />}
            </button>
          );
        })}
        {(!realUser.is_td || !realUser.is_coach) && <div className="border-t border-pb-border/60 mt-1 pt-1">
          <div className="px-3 pt-1 pb-0.5 text-[9px] uppercase tracking-[0.25em] text-zinc-600 font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Add a hat</div>
          {!realUser.is_td && (
            <button data-testid="become-td-btn" onClick={() => { setOpen(false); navigate("/tournaments"); toast.info("Scroll down on Tournaments — the Director application is at the bottom"); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-[11px] text-zinc-400 hover:text-pb-red hover:bg-white/5 transition-colors">
              <ClipboardList className="w-3.5 h-3.5" /> Become a Tournament Director
            </button>
          )}
          {!realUser.is_coach && (
            <button data-testid="become-coach-btn" onClick={() => { setOpen(false); navigate("/community"); toast.info("The Coach application is on the Community page"); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 pb-2.5 text-[11px] text-zinc-400 hover:text-amber-400 hover:bg-white/5 transition-colors">
              <GraduationCap className="w-3.5 h-3.5" /> Become a Coach
            </button>
          )}
        </div>}
      </div>
    </div>
  );
}
